import { Building2, ChevronDown, ChevronRight, Search, UserRound } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { encodeScope } from "@/lib/scope";
import { cn } from "@/lib/utils";
import type {
	KrNode,
	OrphanGroupNode,
	PharmacyNode,
	ScopeRef,
	ScopeTreeResponse,
	SpolkaNode,
} from "./types";
import { useScopeTree } from "./use-scope-tree";

interface ScopePickerProps {
	value: ScopeRef | null;
	onChange: (scope: ScopeRef) => void;
	persistKey?: string;
	className?: string;
	/**
	 * Container dla Portal popovera. Ustaw na wezel WEWNATRZ Radix Dialog/Sheet,
	 * inaczej focus-trap rodzica traktuje portalowany (do body) input jako
	 * "outside" i nie wpuszcza do niego klawiszy. Patrz ui/popover.tsx.
	 */
	portalContainer?: HTMLElement | null;
	/**
	 * Tryb wyboru POJEDYNCZEJ apteki (leaf-only). Naglowki grup
	 * (org/wlasne/franczyza/region/spolka) sluza tylko do rozwijania drzewa —
	 * klikalne sa wylacznie pojedyncze apteki. Uzywany tam, gdzie pole odwzorowuje
	 * jeden `idfirm` (np. przypisanie protokolu receptury do apteki), a nie scope.
	 */
	pharmacyOnly?: boolean;
	/**
	 * Gdy ustawione (w parze z `pharmacyOnly`), na gorze listy pojawia sie wpis
	 * czyszczacy wybor (np. "Bez przypisania"); klikniecie wola `onClear`.
	 */
	clearLabel?: string;
	onClear?: () => void;
	/**
	 * Pokazuje sekcję „Kierownicy regionalni" (wybór JEDNEGO KR). Opt-in:
	 * strony spoza dashboardu nie obsługują jeszcze tokenu `kr:` w swoich
	 * licznikach/etykietach, więc nie dostają tej sekcji domyślnie.
	 */
	withKr?: boolean;
}

/**
 * Pure label resolver. Pass `tree` to get human-readable spolka/apteka/orphan
 * names (lookup po nip/idfirm); bez tree zwraca placeholder z id.
 */
export function scopeLabel(s: ScopeRef, tree?: ScopeTreeResponse): string {
	switch (s.type) {
		case "org":
			return "Cala organizacja";
		case "wlasne":
			return "Wlasne";
		case "franczyza":
			return "Franczyza";
		case "region":
			return s.id ? `Region: ${s.id}` : "Region: bez regionu";
		case "spolka": {
			// V5: spolka identity = znormalizowany NIP (string). Lookup po
			// `nip`, fallback "Spolka NIP <id>" gdy niedopasowane.
			const sp = tree?.spolki.find((x) => x.nip === s.id);
			if (sp) return sp.name;
			return `Spolka NIP ${s.id}`;
		}
		case "apteka": {
			if (tree) {
				for (const r of tree.wlasne.regions) {
					const p = r.pharmacies.find((x) => x.idfirm === s.id);
					if (p) return p.name;
				}
				for (const sp of tree.spolki) {
					const p = sp.pharmacies.find((x) => x.idfirm === s.id);
					if (p) return p.name;
				}
				for (const o of tree.orphanGroups) {
					const p = o.pharmacies.find((x) => x.idfirm === s.id);
					if (p) return p.name;
				}
			}
			return `Apteka #${s.id}`;
		}
		case "orphan": {
			if (tree && s.id) {
				const o = tree.orphanGroups.find((x) => x.nip === s.id);
				if (o) return o.nip ? `Bez spolki (NIP ${o.nip})` : "Bez spolki";
			}
			return s.id ? `Bez spolki (${s.id})` : "Bez spolki";
		}
		case "kr":
			return `KR: ${s.id}`;
	}
}

/**
 * Hook variant — auto-pulls tree z React Query (5min cache). Uzywaj w
 * komponentach ktore juz nie maja tree pod reka (header/subtitle).
 */
export function useScopeLabel(s: ScopeRef): string {
	const { data: tree } = useScopeTree();
	return scopeLabel(s, tree);
}

function pharmacyMatches(p: PharmacyNode, q: string): boolean {
	if (String(p.idfirm).includes(q)) return true;
	if (p.name.toLowerCase().includes(q)) return true;
	if (p.city?.toLowerCase().includes(q)) return true;
	return false;
}

export function ScopePicker({
	value,
	onChange,
	persistKey,
	className,
	portalContainer,
	pharmacyOnly = false,
	clearLabel,
	onClear,
	withKr = false,
}: ScopePickerProps) {
	const { data: tree } = useScopeTree();
	const [open, setOpen] = useState(false);
	const [search, setSearch] = useState("");
	const searchRef = useRef<HTMLInputElement>(null);
	// Top-level (wlasne, franczyza) — Set, oba moga byc niezalezne expanded.
	// Sub-level (region:*, spolka:*) — single string accordion behavior:
	// rozwijanie jednego sub-wezla zamyka pozostale, aby uniknac chaosu wszystkich
	// regionow/spolek otwartych jednoczesnie.
	const [expandedTop, setExpandedTop] = useState<Set<string>>(
		new Set(["wlasne", "franczyza", "kr"]),
	);
	const [expandedSub, setExpandedSub] = useState<string | null>(null);

	const toggle = (key: string) => {
		if (key === "wlasne" || key === "franczyza" || key === "kr") {
			setExpandedTop((s) => {
				const n = new Set(s);
				if (n.has(key)) n.delete(key);
				else n.add(key);
				return n;
			});
		} else {
			setExpandedSub((curr) => (curr === key ? null : key));
		}
	};

	const select = (s: ScopeRef) => {
		onChange(s);
		if (persistKey) localStorage.setItem(persistKey, encodeScope(s));
		setOpen(false);
		setSearch("");
	};

	// W trybie pharmacyOnly naglowek grupy nie jest selektywny — pelni tylko role
	// rozwiniecia galezi (jak chevron). Poza tym trybem zachowuje sie jak dotad.
	const selectGroup = (key: string, s: ScopeRef) => {
		if (pharmacyOnly) toggle(key);
		else select(s);
	};

	const clear = () => {
		onClear?.();
		setOpen(false);
		setSearch("");
	};

	const q = search.trim().toLowerCase();

	// Filtrowane widoki (memoizowane, przebudowywane tylko gdy zmienia sie tree
	// albo q). Gdy search jest puste — wracamy do oryginalnej struktury i
	// zwykly stan expand/collapse decyduje. Gdy search niepuste — filtrujemy
	// drzewo, ukrywajac niepasujace galezie i auto-rozwijajac przodkow.
	const filteredRegions = useMemo(() => {
		if (!tree) return [];
		if (!q) return tree.wlasne.regions;
		return tree.wlasne.regions
			.map((r) => {
				const regionNameMatches = r.name?.toLowerCase().includes(q) ?? "bez regionu".includes(q);
				const matchedPharmacies = r.pharmacies.filter((p) => pharmacyMatches(p, q));
				if (regionNameMatches) {
					// Cala grupa pasuje — pokaz wszystkie apteki w niej.
					return { ...r };
				}
				if (matchedPharmacies.length > 0) {
					return { ...r, pharmacies: matchedPharmacies };
				}
				return null;
			})
			.filter((r): r is NonNullable<typeof r> => r !== null);
	}, [tree, q]);

	const filteredSpolki = useMemo<SpolkaNode[]>(() => {
		if (!tree) return [];
		if (!q) return tree.spolki;
		return tree.spolki
			.map((s) => {
				// V5: nip jest non-nullable string (klucz spolki). Filtr zarowno
				// po nazwie jak i po NIP-ie (uzytkownik moze szukac "9211...").
				const spolkaMatches = s.name.toLowerCase().includes(q) || s.nip.toLowerCase().includes(q);
				const matchedPharmacies = s.pharmacies.filter((p) => pharmacyMatches(p, q));
				if (spolkaMatches) return { ...s };
				if (matchedPharmacies.length > 0) {
					return { ...s, pharmacies: matchedPharmacies };
				}
				return null;
			})
			.filter((s): s is SpolkaNode => s !== null);
	}, [tree, q]);

	const filteredKr = useMemo<KrNode[]>(() => {
		if (!tree || !withKr) return [];
		const list = tree.kierownicy ?? [];
		if (!q) return list;
		return list.filter((k) => k.name.toLowerCase().includes(q));
	}, [tree, q, withKr]);

	const filteredOrphans = useMemo<OrphanGroupNode[]>(() => {
		if (!tree) return [];
		if (!q) return tree.orphanGroups;
		return tree.orphanGroups
			.map((o) => {
				const groupMatches = o.nip ? o.nip.toLowerCase().includes(q) : false;
				const matchedPharmacies = o.pharmacies.filter((p) => pharmacyMatches(p, q));
				if (groupMatches) return { ...o };
				if (matchedPharmacies.length > 0) {
					return { ...o, pharmacies: matchedPharmacies };
				}
				return null;
			})
			.filter((o): o is OrphanGroupNode => o !== null);
	}, [tree, q]);

	// Male drzewo (uzytkownik widzi lacznie niewiele aptek) — rozwijamy cale
	// drzewo od razu, zeby apteki byly widoczne bez klikania w galezie.
	// ponytail: przy auto-expand chevrony nie zwijaja galezi — przy <=10
	// pozycjach zwijanie nie jest potrzebne; jesli kiedys bedzie, przejsc na
	// Set z overrides zamiast short-circuita.
	const autoExpandAll = useMemo(() => {
		if (!tree) return false;
		const total =
			tree.wlasne.regions.reduce((n, r) => n + r.pharmacies.length, 0) +
			tree.spolki.reduce((n, s) => n + s.pharmacies.length, 0) +
			tree.orphanGroups.reduce((n, o) => n + o.pharmacies.length, 0);
		return total > 0 && total <= 10;
	}, [tree]);

	// Gdy search aktywny, wszystkie pasujace galezie maja byc rozwiniete —
	// uzytkownik widzi pelna sciezke (region → apteka, spolka → apteka).
	const isExpanded = (key: string) => {
		if (q || autoExpandAll) return true;
		if (key === "wlasne" || key === "franczyza" || key === "kr") return expandedTop.has(key);
		return expandedSub === key;
	};

	const renderPharmacy = (p: PharmacyNode) => (
		<li key={p.idfirm}>
			<button
				type="button"
				onClick={() => select({ type: "apteka", id: p.idfirm })}
				className={cn(
					"w-full text-left px-2 py-1 rounded hover:bg-muted text-muted-foreground",
					value?.type === "apteka" && value.id === p.idfirm && "bg-muted text-foreground",
				)}
				title={p.city ? `${p.name} - ${p.city}` : p.name}
			>
				<span className="truncate block">
					{p.name}
					{p.city ? <span className="text-[10px] opacity-70 ml-1">({p.city})</span> : null}
				</span>
			</button>
		</li>
	);

	// V5: galaz Wlasne pokazujemy tylko gdy user ma jakikolwiek wlasne apteki
	// w tree (idfirms.length > 0). Konto z dostepem tylko do franczyzy nie
	// powinno widziec pustej galezi "Wlasne". Analogicznie Franczyza.
	const hasAnyWlasne = (tree?.wlasne.idfirms.length ?? 0) > 0;
	const hasAnyFranczyza = (tree?.franczyzaIdfirms.length ?? 0) > 0;
	const hasFranczyzaContent =
		hasAnyFranczyza && (filteredSpolki.length > 0 || filteredOrphans.length > 0);
	const hasWlasneContent = hasAnyWlasne && filteredRegions.length > 0;
	// "Cala organizacja" przycisk nie ma sub-wezlow ani searchable label,
	// wiec przy aktywnym filtrze (q != "") traktujemy go jak nieobecny —
	// noResults pokazujemy gdy zadne galezie scope nie pasuja.
	const hasKrContent = withKr && !pharmacyOnly && filteredKr.length > 0;
	const noResults = q.length > 0 && !hasFranczyzaContent && !hasWlasneContent && !hasKrContent;

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<button
					type="button"
					aria-label="scope picker"
					className={cn(
						"inline-flex items-center gap-2 rounded-md border border-border px-3 h-8 text-xs",
						className,
					)}
				>
					<Building2 className="size-3.5" />
					{value ? scopeLabel(value, tree) : (clearLabel ?? "—")}
					<ChevronDown className="size-3.5" />
				</button>
			</PopoverTrigger>
			<PopoverContent
				portalContainer={portalContainer}
				className="w-80 p-0 max-h-[28rem] flex flex-col pointer-events-auto"
				align="start"
				onOpenAutoFocus={(e) => {
					// Wewnatrz Dialog/Sheet focus-trap rodzica nie wpuszcza klawiszy do
					// portalowanego inputu (TRAP Radix popover-in-modal). Przejmujemy
					// autofocus i recznie ustawiamy go na pole wyszukiwania. setTimeout(0)
					// (a NIE rAF) to sprawdzony timing z distinct-filter-combobox.tsx —
					// odpala sie PO tym, jak focus-trap rodzica zwolni focus.
					e.preventDefault();
					setTimeout(() => searchRef.current?.focus(), 0);
				}}
				// Bez tego focus-trap rodzica (Dialog) przy zamknieciu popovera moze
				// znow porwac focus i zablokowac kolejne otwarcie/wpisywanie.
				onCloseAutoFocus={(e) => e.preventDefault()}
			>
				<div className="p-2 border-b border-border flex items-center gap-2">
					<Search className="size-3.5 text-muted-foreground shrink-0" />
					<input
						ref={searchRef}
						type="text"
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						placeholder="Szukaj regionu, apteki, spolki..."
						aria-label="szukaj w scope"
						className="w-full bg-transparent text-xs outline-none placeholder:text-muted-foreground"
					/>
				</div>
				<div className="overflow-auto flex-1">
					{tree ? (
						<ul className="text-xs p-1">
							{/* ── Wpis czyszczacy wybor (pharmacyOnly) ── */}
							{clearLabel && (
								<li>
									<div className="flex items-center">
										{/* spacer matching chevron icon width — wpis nie ma sub-wezlow */}
										<span className="p-1 inline-block size-5" aria-hidden />
										<button
											type="button"
											onClick={clear}
											className={cn(
												"flex-1 text-left px-1 py-1 rounded hover:bg-muted font-medium",
												value === null && "bg-muted",
											)}
										>
											{clearLabel}
										</button>
									</div>
								</li>
							)}
							{/* ── Top-level: Cala organizacja (super_admin) ── */}
							{tree.orgVisible && !pharmacyOnly && (
								<li>
									<div className="flex items-center">
										{/* spacer matching chevron icon width — "org" nie ma sub-wezlow */}
										<span className="p-1 inline-block size-5" aria-hidden />
										<button
											type="button"
											onClick={() => select({ type: "org" })}
											className={cn(
												"flex-1 text-left px-1 py-1 rounded hover:bg-muted font-medium",
												value?.type === "org" && "bg-muted",
											)}
										>
											Cala organizacja
										</button>
									</div>
								</li>
							)}
							{/* ── Top-level: Wlasne ── */}
							{hasWlasneContent && (
								<li>
									<div className="flex items-center">
										<button
											type="button"
											aria-label="expand wlasne"
											onClick={() => toggle("wlasne")}
											className="p-1"
										>
											{isExpanded("wlasne") ? (
												<ChevronDown className="size-3" />
											) : (
												<ChevronRight className="size-3" />
											)}
										</button>
										<button
											type="button"
											onClick={() => selectGroup("wlasne", { type: "wlasne" })}
											className="flex-1 text-left px-1 py-1 rounded hover:bg-muted font-medium"
										>
											Wlasne
										</button>
									</div>
									{isExpanded("wlasne") && (
										<ul className="ml-3">
											{filteredRegions.map((r) => {
												const key = `region:${r.name ?? ""}`;
												const label = r.name ?? "Bez regionu";
												return (
													<li key={key}>
														<div className="flex items-center">
															<button
																type="button"
																aria-label={`expand region ${label}`}
																onClick={() => toggle(key)}
																className="p-1"
															>
																{isExpanded(key) ? (
																	<ChevronDown className="size-3" />
																) : (
																	<ChevronRight className="size-3" />
																)}
															</button>
															<button
																type="button"
																onClick={() => selectGroup(key, { type: "region", id: r.name })}
																className="flex-1 text-left px-1 py-1 rounded hover:bg-muted"
															>
																{label}
															</button>
														</div>
														{isExpanded(key) && (
															<ul className="ml-5">{r.pharmacies.map(renderPharmacy)}</ul>
														)}
													</li>
												);
											})}
										</ul>
									)}
								</li>
							)}
							{/* ── Top-level: Franczyza ── */}
							{hasFranczyzaContent && (
								<li>
									<div className="flex items-center">
										<button
											type="button"
											aria-label="expand franczyza"
											onClick={() => toggle("franczyza")}
											className="p-1"
										>
											{isExpanded("franczyza") ? (
												<ChevronDown className="size-3" />
											) : (
												<ChevronRight className="size-3" />
											)}
										</button>
										<button
											type="button"
											onClick={() => selectGroup("franczyza", { type: "franczyza" })}
											className="flex-1 text-left px-1 py-1 rounded hover:bg-muted font-medium"
										>
											Franczyza
										</button>
									</div>
									{isExpanded("franczyza") && (
										<ul className="ml-3">
											{filteredSpolki.map((s) => {
												// V5: stable key = znormalizowany NIP z firm.nrnip (nie group_id)
												// — spolka identity = NIP firmowy. pharmacy_groups w produkcji
												// ma 425 dynamic auto-rule groups duplikujacych apteki, NIP
												// jest deterministycznym kluczem.
												const key = `spolka:${s.nip}`;
												return (
													<li key={s.nip}>
														<div className="flex items-center">
															<button
																type="button"
																aria-label={`expand spolka ${s.name}`}
																onClick={() => toggle(key)}
																className="p-1"
															>
																{isExpanded(key) ? (
																	<ChevronDown className="size-3" />
																) : (
																	<ChevronRight className="size-3" />
																)}
															</button>
															<button
																type="button"
																onClick={() => selectGroup(key, { type: "spolka", id: s.nip })}
																className="flex-1 text-left px-1 py-1 rounded hover:bg-muted"
																title={`NIP ${s.nip}`}
															>
																<span className="truncate block">{s.name}</span>
															</button>
														</div>
														{isExpanded(key) && (
															<ul className="ml-5">{s.pharmacies.map(renderPharmacy)}</ul>
														)}
													</li>
												);
											})}
											{filteredOrphans.map((o) => {
												const orphanKey = `orphan:${o.nip ?? "no-nip"}`;
												return (
													<li key={orphanKey}>
														<div className="flex items-center">
															<button
																type="button"
																aria-label={`expand orphan ${o.nip ?? "bez NIP"}`}
																onClick={() => toggle(orphanKey)}
																className="p-1"
															>
																{isExpanded(orphanKey) ? (
																	<ChevronDown className="size-3" />
																) : (
																	<ChevronRight className="size-3" />
																)}
															</button>
															<button
																type="button"
																onClick={() =>
																	selectGroup(orphanKey, { type: "orphan", id: o.nip })
																}
																className="flex-1 text-left px-1 py-1 rounded hover:bg-muted"
															>
																{o.nip ? `Bez spolki (NIP ${o.nip})` : "Bez spolki"}
															</button>
														</div>
														{isExpanded(orphanKey) && (
															<ul className="ml-5">{o.pharmacies.map(renderPharmacy)}</ul>
														)}
													</li>
												);
											})}
										</ul>
									)}
								</li>
							)}
							{/* ── Wymiar: Kierownicy regionalni (opt-in) ── */}
							{hasKrContent && (
								<li>
									<div className="flex items-center">
										<button
											type="button"
											aria-label="expand kr"
											onClick={() => toggle("kr")}
											className="p-1"
										>
											{isExpanded("kr") ? (
												<ChevronDown className="size-3" />
											) : (
												<ChevronRight className="size-3" />
											)}
										</button>
										<button
											type="button"
											onClick={() => toggle("kr")}
											className="flex-1 text-left px-1 py-1 rounded hover:bg-muted font-medium inline-flex items-center gap-1"
										>
											<UserRound className="size-3" />
											Kierownicy regionalni
										</button>
									</div>
									{isExpanded("kr") && (
										<ul className="ml-3">
											{filteredKr.map((k) => (
												<li key={k.name}>
													<button
														type="button"
														onClick={() => select({ type: "kr", id: k.name })}
														className={cn(
															"w-full text-left px-2 py-1 rounded hover:bg-muted",
															value?.type === "kr" && value.id === k.name && "bg-muted font-medium",
														)}
														title={`${k.idfirms.length} aptek`}
													>
														<span className="truncate block">
															{k.name}
															<span className="text-[10px] opacity-70 ml-1">
																({k.idfirms.length})
															</span>
														</span>
													</button>
												</li>
											))}
										</ul>
									)}
								</li>
							)}
							{noResults && (
								<li className="px-2 py-3 text-xs text-muted-foreground text-center">
									Brak wynikow
								</li>
							)}
						</ul>
					) : (
						<div className="px-2 py-3 text-xs text-muted-foreground">Ladowanie...</div>
					)}
				</div>
			</PopoverContent>
		</Popover>
	);
}
